import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Calendar,
  Clock,
  Users,
  DollarSign,
  TrendingUp,
  Search,
  RefreshCw,
  Play,
  UserCheck,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Plus,
  Trash2,
  X,
  Loader2,
  Sparkles
} from 'lucide-react';
import api, { userService } from '../services/api';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

const PortfolioBacklogPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Estados de filtro e busca
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [showPredictiveModal, setShowPredictiveModal] = useState(false);
  const [predictiveData, setPredictiveData] = useState(null);
  const [loadingPredictive, setLoadingPredictive] = useState(false);

  // Estados do Modal de Cronograma (7 Etapas)
  const [scheduleInputs, setScheduleInputs] = useState({
    start_date: new Date().toISOString().split('T')[0],
    total_chapas: 12,
    total_fita_metros: 300,
    estimated_project_hours: 32,
    estimated_assembly_hours: 24,
    num_designers: 1,
    num_assemblers: 2
  });
  const [calculatedSteps, setCalculatedSteps] = useState([]);

  // Estados do Modal de Equipe Dinâmica (Squad Builder)
  const [dynamicTeamList, setDynamicTeamList] = useState([]);

  // Query para buscar projetos em carteira
  const { data: backlogResponse, isLoading, refetch } = useQuery({
    queryKey: ['portfolio-backlog', searchTerm],
    queryFn: async () => {
      const res = await api.get('/portfolio-backlog/backlog', {
        params: { search: searchTerm }
      });
      return res.data;
    },
    staleTime: 15000
  });

  // Query para buscar usuários para alocação de equipe
  const { data: usersResponse } = useQuery({
    queryKey: ['users-team-select'],
    queryFn: () => userService.getUsers({ limit: 100, active: true }),
    staleTime: 5 * 60 * 1000
  });

  const backlogData = backlogResponse?.data?.projects || [];
  const summary = backlogResponse?.data?.summary || {
    total_projects: 0,
    total_financial_volume: 0,
    total_backlog_hours: 0,
    factory_capacity_hours: 480,
    factory_occupancy_percentage: 0
  };

  const usersList = useMemo(() => {
    if (!usersResponse) return [];
    if (Array.isArray(usersResponse?.data?.users)) return usersResponse.data.users;
    if (Array.isArray(usersResponse?.data?.data?.users)) return usersResponse.data.data.users;
    if (Array.isArray(usersResponse?.data)) return usersResponse.data;
    return [];
  }, [usersResponse]);

  // Mutation para calcular/salvar cronograma de 7 etapas
  const calculateScheduleMutation = useMutation({
    mutationFn: async ({ surveyId, payload }) => {
      const res = await api.post(`/portfolio-backlog/surveys/${surveyId}/calculate-schedule`, payload);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success('Cronograma das 7 macroetapas calculado com sucesso!');
      if (data.data) setCalculatedSteps(data.data);
      queryClient.invalidateQueries(['portfolio-backlog']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao calcular cronograma');
    }
  });

  // Mutation para alocar equipe
  const assignTeamMutation = useMutation({
    mutationFn: async ({ surveyId, assignments }) => {
      const res = await api.post(`/portfolio-backlog/surveys/${surveyId}/assign-team`, { assignments });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Equipe multidisciplinar alocada com sucesso!');
      setShowTeamModal(false);
      setSelectedSurvey(null);
      queryClient.invalidateQueries(['portfolio-backlog']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao alocar equipe');
    }
  });

  // Mutation para liberar para a fábrica (PROJ-2026-XXX)
  const releaseMutation = useMutation({
    mutationFn: async (surveyId) => {
      const res = await api.post(`/portfolio-backlog/surveys/${surveyId}/release-to-production`);
      return res.data;
    },
    onSuccess: (data) => {
      const code = data.data?.project_code || 'Projeto';
      toast.success(`🚀 ${code} liberado para produção na fábrica!`);
      setShowReleaseModal(false);
      setSelectedSurvey(null);
      queryClient.invalidateQueries(['portfolio-backlog']);
      queryClient.invalidateQueries(['projects']);
      navigate('/projects');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao liberar projeto para a fábrica');
    }
  });

  // Abrir modal de cronograma
  const handleOpenSchedule = (survey) => {
    setSelectedSurvey(survey);
    if (survey.planned_schedule && survey.planned_schedule.length > 0) {
      setCalculatedSteps(survey.planned_schedule);
    } else {
      setCalculatedSteps([]);
    }
    setShowScheduleModal(true);
  };

  // Executar cálculo de cronograma
  const handleCalculateSchedule = (e) => {
    e.preventDefault();
    if (!selectedSurvey) return;
    calculateScheduleMutation.mutate({
      surveyId: selectedSurvey.id,
      payload: scheduleInputs
    });
  };

  // Abrir modal do Motor Preditivo
  const handleOpenPredictive = async (survey) => {
    setSelectedSurvey(survey);
    setShowPredictiveModal(true);
    setLoadingPredictive(true);
    setPredictiveData(null);
    try {
      const res = await api.get(`/portfolio-backlog/surveys/${survey.id}/predict-team-hours`);
      if (res.data?.success) {
        setPredictiveData(res.data.data);
      }
    } catch (err) {
      toast.error('Erro ao consultar motor preditivo: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingPredictive(false);
    }
  };

  // Aplicar Sugestão Preditiva (Aloca equipe e calcula 7 etapas)
  const handleApplyPredictive = async () => {
    if (!selectedSurvey || !predictiveData) return;
    try {
      const suggestedTeam = predictiveData.suggested_team || [];
      const assignments = suggestedTeam.map(st => ({
        role: st.role,
        user_id: st.user_id
      })).filter(a => a.user_id);

      // 1. Alocar equipe recomendada
      await assignTeamMutation.mutateAsync({
        surveyId: selectedSurvey.id,
        assignments
      });

      // 2. Calcular cronograma das 7 etapas com parâmetros preditivos
      const params = predictiveData.predicted_parameters || {};
      await calculateScheduleMutation.mutateAsync({
        surveyId: selectedSurvey.id,
        payload: {
          start_date: new Date().toISOString().split('T')[0],
          total_chapas: params.total_chapas || 12,
          total_fita_metros: params.total_fita_metros || 300,
          estimated_project_hours: params.estimated_project_hours || 32,
          estimated_assembly_hours: params.estimated_assembly_hours || 24,
          num_designers: params.num_designers || 1,
          num_assemblers: params.num_assemblers || 2
        }
      });

      toast.success('⚡ Equipe e Prazos sugeridos pelo Motor Preditivo foram aplicados com sucesso!');
      setShowPredictiveModal(false);
      setSelectedSurvey(null);
    } catch (err) {
      toast.error('Erro ao aplicar sugestão preditiva: ' + (err.response?.data?.message || err.message));
    }
  };

  // Abrir modal de equipe (Construtor Dinâmico de Squads)
  const handleOpenTeam = (survey) => {
    setSelectedSurvey(survey);
    const team = survey.team_assignments || [];
    if (team.length > 0) {
      setDynamicTeamList(team.map(t => ({
        role: t.role || 'MARCENEIRO_LIDER',
        user_id: t.user_id ? String(t.user_id) : '',
        allocated_hours: t.allocated_hours || 24,
        is_leader: t.is_leader || false,
        notes: t.notes || ''
      })));
    } else {
      // Padrão inicial: 1 Projetista Líder, 1 Marceneiro Líder, 1 Montador
      setDynamicTeamList([
        { role: 'PROJETISTA_LIDER', user_id: '', allocated_hours: 32, is_leader: true, notes: '' },
        { role: 'MARCENEIRO_LIDER', user_id: '', allocated_hours: 40, is_leader: true, notes: '' },
        { role: 'MONTADOR_EXTERNO', user_id: '', allocated_hours: 24, is_leader: true, notes: '' }
      ]);
    }
    setShowTeamModal(true);
  };

  // Adicionar novo profissional ao Squad
  const handleAddTeamMember = () => {
    setDynamicTeamList(prev => [
      ...prev,
      { role: 'MARCENEIRO_AUX', user_id: '', allocated_hours: 20, is_leader: false, notes: '' }
    ]);
  };

  // Remover profissional do Squad
  const handleRemoveTeamMember = (index) => {
    setDynamicTeamList(prev => prev.filter((_, idx) => idx !== index));
  };

  // Atualizar campo do profissional no Squad
  const handleUpdateTeamMember = (index, field, value) => {
    setDynamicTeamList(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  // Salvar alocação de equipe dinâmica
  const handleSaveTeam = (e) => {
    e.preventDefault();
    if (!selectedSurvey) return;

    const assignments = dynamicTeamList
      .filter(member => member.user_id)
      .map(member => ({
        role: member.role,
        user_id: parseInt(member.user_id, 10),
        allocated_hours: parseFloat(member.allocated_hours) || 0,
        is_leader: member.is_leader ? true : false,
        notes: member.notes || null
      }));

    if (assignments.length === 0) {
      toast.error('Selecione pelo menos um profissional para a equipe da obra');
      return;
    }

    assignTeamMutation.mutate({
      surveyId: selectedSurvey.id,
      assignments
    });
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [y, m, d] = dateStr.split('T')[0].split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Top Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between py-4 gap-4">
            <div>
              <nav className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                <Link to="/dashboard" className="hover:text-gray-700 transition-colors">Início</Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-[#49943d] font-semibold">Projetos em Carteira</span>
              </nav>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-700 shadow-xs">
                  <Briefcase className="w-5 h-5" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                    Projetos em Carteira (PCP & Backlog)
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Projetos aprovados aguardando cálculo de prazos e alocação de equipe antes do envio para a fábrica.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => refetch()}
                className="px-3.5 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Atualizar</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-6">

        {/* 4 Cards de Indicadores (KPIs do Backlog) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-xs">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 text-purple-700 rounded-lg">
                <Briefcase className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Em Carteira</p>
                <p className="text-2xl font-bold text-gray-900">{summary.total_projects}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Obras fechadas aguardando fábrica</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-xs">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 text-emerald-700 rounded-lg">
                <DollarSign className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Volume Retido</p>
                <p className="text-xl font-bold text-emerald-700 truncate">
                  {formatCurrency(summary.total_financial_volume)}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Valor total dos contratos em carteira</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-xs">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 text-blue-700 rounded-lg">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Horas em Espera</p>
                <p className="text-2xl font-bold text-blue-700">{summary.total_backlog_hours}h</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Horas estimadas de marcenaria/montagem</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-xs">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 text-amber-700 rounded-lg">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Ocupação da Fábrica</p>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <p className="text-2xl font-bold text-amber-700">{summary.factory_occupancy_percentage}%</p>
                  <span className="text-xs text-gray-500 font-medium">({summary.factory_capacity_hours}h max)</span>
                </div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2 overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${
                  summary.factory_occupancy_percentage > 90 ? 'bg-red-500' :
                  summary.factory_occupancy_percentage > 70 ? 'bg-amber-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(100, summary.factory_occupancy_percentage)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Filtros e Busca */}
        <div className="bg-white rounded-xl shadow-xs border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="relative w-full sm:w-96">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por cliente, código ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#49943d]"
              />
            </div>
            <span className="text-xs font-semibold text-gray-500">
              Mostrando {backlogData.length} pedido(s) em carteira
            </span>
          </div>
        </div>

        {/* Tabela de Projetos em Carteira */}
        <div className="bg-white rounded-xl shadow-xs border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center text-gray-500 flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-[#49943d]" />
              <p>Carregando projetos em carteira...</p>
            </div>
          ) : backlogData.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-base font-semibold text-gray-700">Nenhum projeto em carteira no momento</p>
              <p className="text-xs text-gray-500 mt-1">
                Quando a proposta comercial de um levantamento for aprovada pelo cliente, o pedido aparecerá nesta esteira de PCP.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ações</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Cliente</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Orçamento</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Ambientes</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Cronograma (7 Etapas)</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Equipe Alocada</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {backlogData.map((survey) => {
                    const hasSchedule = survey.planned_schedule && survey.planned_schedule.length > 0;
                    const hasTeam = survey.team_assignments && survey.team_assignments.length > 0;
                    const budgetValue = parseFloat(survey.budget_presented) || parseFloat(survey.budget_total_price) || 0;

                    return (
                      <tr key={survey.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleOpenPredictive(survey)}
                              className="px-2.5 py-1.5 text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-all flex items-center gap-1 text-xs font-bold shadow-2xs"
                              title="Motor Preditivo: Sugerir Equipe e Horas por Orçamento"
                            >
                              <Sparkles className="w-3.5 h-3.5 text-purple-600 animate-pulse" />
                              <span>IA Preditiva</span>
                            </button>
                            <button
                              onClick={() => handleOpenSchedule(survey)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1 text-xs font-medium"
                              title="Calcular/Editar Prazos das 7 Macroetapas"
                            >
                              <Clock className="w-4 h-4" />
                              <span>Prazos</span>
                            </button>
                            <button
                              onClick={() => handleOpenTeam(survey)}
                              className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center gap-1 text-xs font-medium"
                              title="Alocar Equipe Multidisciplinar"
                            >
                              <Users className="w-4 h-4" />
                              <span>Equipe</span>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedSurvey(survey);
                                setShowReleaseModal(true);
                              }}
                              disabled={!hasSchedule || !hasTeam}
                              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1 shadow-2xs ${
                                hasSchedule && hasTeam
                                  ? 'bg-[#49943d] text-white hover:bg-[#3a7630]'
                                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              }`}
                              title={!hasSchedule || !hasTeam ? 'Calcule os prazos e aloque a equipe antes de liberar' : 'Liberar para Ordem de Corte na Fábrica'}
                            >
                              <Play className="w-3.5 h-3.5 fill-current" />
                              <span>Liberar</span>
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap font-bold text-gray-900">
                          {survey.survey_code || `#${survey.id}`}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{survey.client_name}</div>
                          <div className="text-xs text-gray-500">{survey.client_phone || survey.client_address || 'Sem telefone'}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right font-bold text-emerald-700">
                          {formatCurrency(budgetValue)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
                            {survey.environment_count || 1} ambiente(s)
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {hasSchedule ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                              <CheckCircle2 className="w-3.5 h-3.5" /> 7 Etapas Prontas
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                              <AlertCircle className="w-3.5 h-3.5" /> Pendente
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {hasTeam ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
                              <UserCheck className="w-3.5 h-3.5" /> {survey.team_assignments.length} Alocados
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                              <AlertCircle className="w-3.5 h-3.5" /> Pendente
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal 1: Motor de Prazos (7 Macroetapas Sequenciais em Dias Úteis) */}
      {showScheduleModal && selectedSurvey && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6 space-y-6 max-h-[90vh] overflow-y-auto border border-gray-100">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Cálculo de Prazos (7 Macroetapas) — {selectedSurvey.survey_code}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Cliente: {selectedSurvey.client_name} • Cálculo sequencial em dias úteis reais
                  </p>
                </div>
              </div>
              <button onClick={() => setShowScheduleModal(false)} className="p-2 text-gray-400 hover:text-gray-700 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Formulário de Parâmetros de Entrada */}
            <form onSubmit={handleCalculateSchedule} className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-blue-50/50 p-4 rounded-xl border border-blue-100">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Data Início (Medição Fina)</label>
                <input
                  type="date"
                  value={scheduleInputs.start_date}
                  onChange={(e) => setScheduleInputs(p => ({ ...p, start_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Estimativa de Chapas</label>
                <input
                  type="number"
                  value={scheduleInputs.total_chapas}
                  onChange={(e) => setScheduleInputs(p => ({ ...p, total_chapas: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Metros de Fita de Borda</label>
                <input
                  type="number"
                  value={scheduleInputs.total_fita_metros}
                  onChange={(e) => setScheduleInputs(p => ({ ...p, total_fita_metros: parseInt(e.target.value) || 10 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  min="10"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Horas de Desenho (3D/Detalhamento)</label>
                <input
                  type="number"
                  value={scheduleInputs.estimated_project_hours}
                  onChange={(e) => setScheduleInputs(p => ({ ...p, estimated_project_hours: parseFloat(e.target.value) || 8 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Horas de Montagem na Obra</label>
                <input
                  type="number"
                  value={scheduleInputs.estimated_assembly_hours}
                  onChange={(e) => setScheduleInputs(p => ({ ...p, estimated_assembly_hours: parseFloat(e.target.value) || 8 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={calculateScheduleMutation.isPending}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-bold flex items-center justify-center gap-2 shadow-xs"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>{calculateScheduleMutation.isPending ? 'Calculando...' : 'Calcular 7 Etapas'}</span>
                </button>
              </div>
            </form>

            {/* Cronograma Gantt Sequencial de 7 Etapas */}
            {calculatedSteps.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    Cronograma Sequencial em Dias Úteis (Linha do Tempo)
                  </h4>
                  <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
                    Total: {calculatedSteps.reduce((acc, st) => acc + st.estimated_days, 0)} Dias Úteis
                  </span>
                </div>

                <div className="space-y-3 bg-gray-50/70 p-4 rounded-xl border border-gray-200">
                  {calculatedSteps.map((st, i) => {
                    const maxDays = calculatedSteps.reduce((acc, s) => acc + s.estimated_days, 0) || 1;
                    const widthPct = Math.max(10, Math.min(100, Math.round((st.estimated_days / maxDays) * 100)));
                    
                    const stepColors = [
                      'from-blue-500 to-cyan-500',
                      'from-indigo-500 to-purple-500',
                      'from-purple-500 to-pink-500',
                      'from-amber-500 to-orange-500',
                      'from-emerald-500 to-teal-500',
                      'from-teal-500 to-blue-600',
                      'from-green-600 to-emerald-700'
                    ];

                    return (
                      <div key={i} className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-2xs space-y-2">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-800 font-bold text-xs flex items-center justify-center">
                              {st.step_order}
                            </span>
                            <span className="font-bold text-sm text-gray-900">{st.step_name}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md">
                              {st.estimated_days} dia(s) útil(eis) • {st.estimated_hours}h
                            </span>
                            <span className="text-gray-500 font-medium">
                              {formatDate(st.start_date)} até {formatDate(st.end_date)}
                            </span>
                          </div>
                        </div>

                        {/* Barra Gantt Visual */}
                        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden p-0.5">
                          <div
                            className={`h-2 rounded-full bg-gradient-to-r ${stepColors[i % stepColors.length]} transition-all duration-500 shadow-xs`}
                            style={{ width: `${widthPct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowScheduleModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal 2: Alocação de Equipe Multidisciplinar (Squad Builder Dinâmico) */}
      {showTeamModal && selectedSurvey && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-6 max-h-[90vh] overflow-y-auto border border-gray-100">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Montar Squad & Horas — {selectedSurvey.survey_code}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Monte equipes dinâmicas (duplas por cargo, duplas mescladas, unitários ou squads) com atribuição individual de horas
                  </p>
                </div>
              </div>
              <button onClick={() => setShowTeamModal(false)} className="p-2 text-gray-400 hover:text-gray-700 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveTeam} className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Profissionais Alocados no Squad ({dynamicTeamList.length})
                </h4>
                <button
                  type="button"
                  onClick={handleAddTeamMember}
                  className="px-3 py-1.5 bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 rounded-lg text-xs font-bold flex items-center gap-1 transition-all shadow-2xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Adicionar Profissional</span>
                </button>
              </div>

              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                {dynamicTeamList.map((member, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                      <div className="sm:col-span-4">
                        <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Papel / Função</label>
                        <select
                          value={member.role}
                          onChange={(e) => handleUpdateTeamMember(idx, 'role', e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs font-semibold text-gray-800"
                        >
                          <option value="PROJETISTA_LIDER">Projetista Líder (3D)</option>
                          <option value="MARCENEIRO_LIDER">Marceneiro Líder</option>
                          <option value="MARCENEIRO_AUX">Marceneiro Auxiliar</option>
                          <option value="MONTADOR_EXTERNO">Montador Externo</option>
                          <option value="PINTOR_LAQUEADOR">Pintor / Laqueador</option>
                          <option value="AJUDANTE">Ajudante Geral</option>
                        </select>
                      </div>

                      <div className="sm:col-span-5">
                        <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Colaborador *</label>
                        <select
                          value={member.user_id}
                          onChange={(e) => handleUpdateTeamMember(idx, 'user_id', e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs font-semibold text-gray-800 focus:ring-2 focus:ring-purple-500"
                          required
                        >
                          <option value="">Selecione o funcionário...</option>
                          {usersList.map(u => (
                            <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>
                          ))}
                        </select>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">Horas Est.</label>
                        <input
                          type="number"
                          value={member.allocated_hours}
                          onChange={(e) => handleUpdateTeamMember(idx, 'allocated_hours', e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs font-bold text-purple-700 text-center"
                          min="1"
                        />
                      </div>

                      <div className="sm:col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveTeamMember(idx)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remover profissional"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 pt-1 border-t border-gray-200/60 text-xs">
                      <label className="flex items-center gap-2 cursor-pointer font-medium text-gray-700">
                        <input
                          type="checkbox"
                          checked={member.is_leader}
                          onChange={(e) => handleUpdateTeamMember(idx, 'is_leader', e.target.checked)}
                          className="rounded text-purple-600 focus:ring-purple-500"
                        />
                        <span>Líder da Etapa/Setor</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Observação (ex: responsável pelo acabamento fino)..."
                        value={member.notes}
                        onChange={(e) => handleUpdateTeamMember(idx, 'notes', e.target.value)}
                        className="flex-1 px-3 py-1 bg-white border border-gray-200 rounded-md text-xs text-gray-700"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowTeamModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={assignTeamMutation.isPending}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-bold shadow-xs flex items-center gap-2"
                >
                  <Users className="w-4 h-4" />
                  <span>{assignTeamMutation.isPending ? 'Salvando Squad...' : 'Salvar Equipe & Horas'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 4: Motor Preditivo de Equipes e Horas */}
      {showPredictiveModal && selectedSurvey && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-6 max-h-[90vh] overflow-y-auto border border-gray-100">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
                  <Sparkles className="w-5 h-5 text-purple-600 animate-pulse" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    Motor Preditivo de Equipes & Horas — {selectedSurvey.survey_code || `#${selectedSurvey.id}`}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Análise preditiva baseada no valor do contrato ({formatCurrency(parseFloat(selectedSurvey.budget_presented) || parseFloat(selectedSurvey.budget_total_price) || 0)}) e carga horária dos funcionários
                  </p>
                </div>
              </div>
              <button onClick={() => setShowPredictiveModal(false)} className="p-2 text-gray-400 hover:text-gray-700 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingPredictive ? (
              <div className="p-12 text-center text-gray-500 flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                <p className="text-sm font-medium">Analisando tamanho do orçamento e disponibilidade da equipe...</p>
              </div>
            ) : predictiveData ? (
              <div className="space-y-5">
                {/* Resumo Preditivo de Porte */}
                <div className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white p-5 rounded-2xl shadow-md">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div>
                      <span className="inline-block px-2.5 py-0.5 bg-purple-400/30 text-purple-200 rounded-full text-xs font-semibold uppercase tracking-wider mb-1">
                        Porte: {predictiveData.complexity_tier}
                      </span>
                      <h4 className="text-xl font-bold">{predictiveData.model_name}</h4>
                      <p className="text-xs text-purple-200 mt-1">
                        Orçamento: <strong className="text-white">{formatCurrency(predictiveData.budget_value)}</strong> • {predictiveData.environment_count} Ambiente(s)
                      </p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-xl border border-white/20 text-center">
                      <p className="text-xs text-purple-200 uppercase font-semibold">Estrutura</p>
                      <p className="text-sm font-bold text-white">{predictiveData.team_model}</p>
                    </div>
                  </div>
                </div>

                {/* Equipe Sugerida com Base na Disponibilidade */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-600" />
                    Equipe Recomendada (Com Carga Horária Disponível)
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {predictiveData.suggested_team.map((st, idx) => (
                      <div key={idx} className="p-3.5 bg-purple-50/60 rounded-xl border border-purple-100 flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-purple-900">{st.role_label}</p>
                          <p className="text-sm font-bold text-gray-900">{st.user_name}</p>
                        </div>
                        <div className="text-right">
                          <span className="px-2 py-1 bg-purple-200/60 text-purple-800 rounded-md text-xs font-bold">
                            {st.recommended_hours}h est.
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Indicadores Financeiros da Mão de Obra do Squad */}
                {predictiveData.financial_labor_metrics && (
                  <div className="bg-emerald-50/70 p-4 rounded-xl border border-emerald-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-emerald-100 text-emerald-800 rounded-lg">
                        <DollarSign className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-emerald-900 uppercase tracking-wider">Custo Est. Mão de Obra</p>
                        <p className="text-lg font-extrabold text-emerald-700">
                          {formatCurrency(predictiveData.financial_labor_metrics.total_labor_cost)}
                        </p>
                      </div>
                    </div>
                    <div className="sm:text-right">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Impacto no Orçamento</p>
                      <span className="inline-block px-3 py-1 bg-emerald-200 text-emerald-900 font-extrabold text-sm rounded-full mt-0.5">
                        {predictiveData.financial_labor_metrics.labor_margin_percentage}% do Contrato
                      </span>
                    </div>
                  </div>
                )}

                {/* Parâmetros Preditivos Previstos */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    Parâmetros & Horas Preditivas Calculadas
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-center">
                      <p className="text-xs text-gray-500 font-semibold">Estimativa Chapas</p>
                      <p className="text-lg font-bold text-gray-900">{predictiveData.predicted_parameters.total_chapas} un</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-center">
                      <p className="text-xs text-gray-500 font-semibold">Fita de Borda</p>
                      <p className="text-lg font-bold text-gray-900">{predictiveData.predicted_parameters.total_fita_metros} m</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-center">
                      <p className="text-xs text-gray-500 font-semibold">Desenho 3D/Proj</p>
                      <p className="text-lg font-bold text-blue-700">{predictiveData.predicted_parameters.estimated_project_hours}h</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-center">
                      <p className="text-xs text-gray-500 font-semibold">Montagem Obra</p>
                      <p className="text-lg font-bold text-emerald-700">{predictiveData.predicted_parameters.estimated_assembly_hours}h</p>
                    </div>
                  </div>
                </div>

                {/* Botões de Ação do Modal */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowPredictiveModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium"
                  >
                    Fechar
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyPredictive}
                    className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 text-sm font-bold flex items-center gap-2 shadow-md transition-all"
                  >
                    <Sparkles className="w-4 h-4 fill-current" />
                    <span>Aplicar Sugestão Preditiva (Equipe + Prazos)</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                Não foi possível carregar as sugestões preditivas.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal 3: Confirmar Liberação para Fábrica (PROJ-2026-XXX) */}
      {showReleaseModal && selectedSurvey && (
        <ConfirmModal
          isOpen={showReleaseModal}
          onClose={() => setShowReleaseModal(false)}
          onConfirm={() => releaseMutation.mutate(selectedSurvey.id)}
          title="Liberar Projeto para Produção na Fábrica"
          message={`Tem certeza que deseja liberar o levantamento ${selectedSurvey.survey_code} para a fábrica? Um código de projeto oficial (PROJ-2026-XXX) será gerado com o cronograma e a equipe alocada.`}
          confirmText="Sim, Liberar para Fábrica"
          cancelText="Cancelar"
          type="success"
        />
      )}
    </div>
  );
};

export default PortfolioBacklogPage;
